import { RefreshCw, Search } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import {
  CLIENT_FILTER_ALL,
  ClientFilterSelect,
  projectMatchesClientFilter,
} from "@/components/client-filter-select";
import { useTableSort } from "@/components/sortable-table-head";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  type ProjectSummary,
  useAdminPromoteProjectToProd,
  useAdminRefreshProjectLastLogins,
  useAdminSetProjectClient,
  useProjects,
  useRefreshProjects,
} from "@/lib/api";
import { formatRelativeTime } from "@/lib/format";
import { logger } from "@/lib/logger";
import { getResourceRemovalState } from "@/lib/project-status";
import { cn } from "@/lib/utils";

import { ProjectArchiveDialog } from "./project-archive-dialog";
import { ProjectDeleteDialog } from "./project-delete-dialog";
import { ProjectTable } from "./project-table";

// Sentinel owner-filter value for projects that have no owner. Cannot collide
// with a real owner email (emails always contain "@").
const OWNER_UNASSIGNED = "__unassigned__";

// Sentinel status-filter value for the derived "partly removed" state. It is not
// a real DB status, so it cannot collide with one.
const STATUS_PARTLY_REMOVED = "__partly_removed__";

// Same trick for archived projects: `shut_down` is a column, not a status, so
// it needs its own sentinel to sit alongside the real statuses in one filter.
const STATUS_ARCHIVED = "__archived__";

// How old the freshest `last_login_checked_at` may get before the page asks the
// backend for a new sweep. The sweep queries every project's own database, so
// it is worth a day's staleness rather than a round trip on every visit.
const LOGIN_CHECK_STALE_MS = 24 * 60 * 60 * 1000;

export function AdminProjectsPage() {
  // Quietest first by default: this view exists to spot abandoned projects, and
  // a project nobody has ever signed into is the one worth finding.
  // Ordering is server-side; the header clicks just change sort_by/sort_dir.
  const { sortBy, sortDir, onSort } = useTableSort({ sortBy: "last_login_at", sortDir: "asc" });
  const {
    data: projects = [],
    isLoading: loading,
    refetch: refetchProjects,
  } = useProjects(true, {
    sortBy: sortBy ?? undefined,
    sortDir,
  });
  const refresh = useRefreshProjects();
  const refreshLastLogins = useAdminRefreshProjectLastLogins();
  // `mutate` is stable across renders while the hook result object is not, so
  // the auto-sweep effect below depends on the function, not the mutation.
  const { mutate: sweepLastLogins } = refreshLastLogins;
  const setClientMutation = useAdminSetProjectClient();
  const promoteMutation = useAdminPromoteProjectToProd();
  const [promotingId, setPromotingId] = useState<number | null>(null);
  const [promoteError, setPromoteError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [ownerFilter, setOwnerFilter] = useState<string>("all");
  const [clientFilter, setClientFilter] = useState<string>(CLIENT_FILTER_ALL);
  const [deleteProject, setDeleteProject] = useState<ProjectSummary | null>(null);
  const [archiveProject, setArchiveProject] = useState<ProjectSummary | null>(null);

  const statuses = useMemo(() => [...new Set(projects.map((p) => p.status))].sort(), [projects]);

  // Distinct owners keyed by email (the stable identifier); the label prefers
  // the display name. Projects without an owner collapse into one "Unknown"
  // bucket under the OWNER_UNASSIGNED sentinel -- matching the table's own
  // no-owner label so the option reads the same as the column.
  const owners = useMemo(() => {
    const byKey = new Map<string, string>();
    for (const p of projects) {
      const key = p.owner_email ?? OWNER_UNASSIGNED;
      if (!byKey.has(key)) {
        byKey.set(key, p.owner_name || p.owner_email || "Unknown");
      }
    }
    return [...byKey.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [projects]);

  // Surfaced in the header so half torn down projects announce themselves
  // instead of having to be hunted for in the table.
  const partlyRemovedCount = useMemo(
    () => projects.filter((p) => getResourceRemovalState(p) !== null).length,
    [projects],
  );

  const archivedCount = useMemo(() => projects.filter((p) => p.shut_down).length, [projects]);

  // The most recent moment we managed to ask a project's database for its last
  // sign-in, which is what the "Last login" column's trustworthiness rests on.
  // Archived projects are skipped: their database is gone, so their timestamp
  // is frozen at the final check and would make a stale page look fresh.
  const freshestLoginCheck = useMemo(() => {
    let newest: string | null = null;
    let newestMs = -Infinity;
    for (const p of projects) {
      if (p.shut_down || !p.last_login_checked_at) {
        continue;
      }
      const ms = new Date(p.last_login_checked_at).getTime();
      if (Number.isNaN(ms) || ms <= newestMs) {
        continue;
      }
      newestMs = ms;
      newest = p.last_login_checked_at;
    }
    return newest;
  }, [projects]);

  // Only live projects can be swept; an all-archived list has nothing to ask.
  const liveCount = projects.length - archivedCount;

  // One sweep per page visit. The ref survives the refetch that the mutation's
  // own invalidation triggers, so a backend that cannot reach a project's
  // database leaves the timestamps stale without putting the page in a loop.
  const sweptLoginsRef = useRef(false);
  useEffect(() => {
    if (sweptLoginsRef.current || loading || liveCount === 0) {
      return;
    }
    const stale =
      freshestLoginCheck === null ||
      Date.now() - new Date(freshestLoginCheck).getTime() > LOGIN_CHECK_STALE_MS;
    if (!stale) {
      return;
    }
    sweptLoginsRef.current = true;
    sweepLastLogins(undefined, {
      onError: (err) => logger.error("[admin-projects] last login sweep failed:", err),
    });
  }, [loading, liveCount, freshestLoginCheck, sweepLastLogins]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    // Ordering is server-side (sortBy/sortDir); only filter here.
    return projects
      .filter((p) => {
        if (statusFilter === "all") {
          return true;
        }
        if (statusFilter === STATUS_PARTLY_REMOVED) {
          return getResourceRemovalState(p) !== null;
        }
        if (statusFilter === STATUS_ARCHIVED) {
          return p.shut_down === true;
        }
        // Archiving leaves `status` untouched, so an archived project would
        // still answer the plain "Completed" filter. Someone filtering by a
        // real status is looking for live projects; the Archived option is the
        // only way to see shut down ones.
        return p.status === statusFilter && p.shut_down !== true;
      })
      .filter((p) => ownerFilter === "all" || (p.owner_email ?? OWNER_UNASSIGNED) === ownerFilter)
      .filter((p) => projectMatchesClientFilter(p, clientFilter))
      .filter(
        (p) =>
          !q ||
          p.project_name.toLowerCase().includes(q) ||
          p.project_slug.toLowerCase().includes(q) ||
          (p.owner_email ?? "").toLowerCase().includes(q) ||
          (p.owner_name ?? "").toLowerCase().includes(q),
      );
  }, [projects, search, statusFilter, ownerFilter, clientFilter]);

  const outOfSync = refresh.data?.out_of_sync ?? [];
  const refreshing = refresh.isPending || refreshLastLogins.isPending;

  // Result of the last sweep this page ran, or null until it runs one.
  const sweep = refreshLastLogins.data;
  const sweepRan = sweep !== null && sweep !== undefined && sweep.already_running !== true;

  // How far the "Last login" column can be trusted right now. The age comes
  // from the stored `last_login_checked_at` values rather than from the last
  // response, so it reports what the column actually holds.
  const loginFreshness = (() => {
    if (refreshLastLogins.isPending) {
      return "Checking logins...";
    }
    if (refreshLastLogins.isError) {
      return "Login check failed";
    }
    const age =
      freshestLoginCheck === null
        ? "Logins never checked"
        : `Logins checked ${formatRelativeTime(freshestLoginCheck).toLowerCase()}`;
    // `already_running` is the one success that checked nothing: another sweep
    // holds the lock and every count comes back zero. Without saying so, a
    // click that did nothing would look like a click that found nothing.
    if (sweep?.already_running === true) {
      return `${age}, another check is running`;
    }
    if (!sweepRan) {
      return age;
    }
    // A partial sweep must not read as a complete one: the line says "just
    // now" while the projects behind these two counts keep a login from
    // whenever they were last reachable. `failed` means we asked and got
    // nothing back; `not_reached` means the budget ran out before we asked at
    // all. Both can be non-zero on one run, so they join into one sentence
    // rather than two competing ones. `deadline_reached` adds nothing here:
    // a budget that expired as the last project finished left every project
    // checked, and a caveat that fires when nothing is wrong teaches people
    // to skim past the ones that matter.
    const caveats: string[] = [];
    if (sweep.failed > 0) {
      caveats.push(`${sweep.failed} did not answer`);
    }
    if (sweep.not_reached > 0) {
      caveats.push(`${sweep.not_reached} not checked in time`);
    }
    return caveats.length > 0 ? `${age}, ${caveats.join(" and ")}` : age;
  })();

  // The full breakdown on hover, so the visible line stays short enough to sit
  // in the filter bar instead of wrapping onto its own row and claiming more
  // weight than a summary deserves. Each count means exactly one thing.
  const loginSweepDetail = sweepRan
    ? [
        `${sweep.checked} asked`,
        `${sweep.updated} stored`,
        `${sweep.failed} did not answer`,
        `${sweep.not_reached} not checked in time`,
        `${sweep.skipped} with nothing to check`,
      ].join(", ") + (sweep.deadline_reached === true ? ". The check hit its time limit." : "")
    : undefined;

  // Two independent checks: the repo check hits GitHub, the login sweep hits
  // every project's database. They run together and report separately, so one
  // failing does not hide the other's result.
  const handleRefresh = async () => {
    const [repos, logins] = await Promise.allSettled([
      refresh.mutateAsync(),
      refreshLastLogins.mutateAsync(),
    ]);
    if (repos.status === "rejected") {
      logger.error("[admin-projects] repo check failed:", repos.reason);
    }
    if (logins.status === "rejected") {
      logger.error("[admin-projects] last login sweep failed:", logins.reason);
    }
  };

  // Provisioning runs server-side for minutes with no push channel, so poll
  // while any project is mid-promotion and stop as soon as none are.
  const anyProvisioning = projects.some((p) => p.prod_status === "provisioning");
  useEffect(() => {
    if (!anyProvisioning) {
      return;
    }
    const timer = setInterval(() => void refetchProjects(), 15_000);
    return () => clearInterval(timer);
  }, [anyProvisioning, refetchProjects]);

  const handlePromoteToProd = async (project: ProjectSummary) => {
    setPromotingId(project.id);
    setPromoteError("");
    try {
      await promoteMutation.mutateAsync({ projectId: project.id });
    } catch (err) {
      logger.error("[admin-projects] promote to prod failed:", err);
      setPromoteError(
        `Could not start the prod promotion for ${project.project_name}. Please try again.`,
      );
    } finally {
      setPromotingId(null);
    }
  };

  const handleSetClient = async (projectId: number, clientId: number | null) => {
    try {
      await setClientMutation.mutateAsync({ projectId, clientId });
    } catch (err) {
      logger.error("[admin-projects] set client failed:", err);
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-primary text-2xl font-normal">Projects</h2>
          <p className="text-muted mt-1 text-sm">
            All scaffolded projects: {projects.length} project
            {projects.length !== 1 ? "s" : ""}, least recently signed into first
            {partlyRemovedCount > 0 && (
              <>
                {", "}
                <button
                  type="button"
                  onClick={() => setStatusFilter(STATUS_PARTLY_REMOVED)}
                  className="font-medium text-orange-700 underline underline-offset-2 dark:text-orange-400"
                >
                  {partlyRemovedCount} partly removed
                </button>
              </>
            )}
          </p>
        </div>
        <Button
          variant="outline"
          onClick={handleRefresh}
          disabled={refreshing}
          className="shrink-0"
        >
          <RefreshCw className={refreshing ? "mr-2 size-4 animate-spin" : "mr-2 size-4"} />
          {refreshing ? "Checking..." : "Check repos and logins"}
        </Button>
      </div>

      {/* Out-of-sync results from the repo check */}
      {refresh.isError && (
        <p className="text-destructive mt-4 text-sm">
          Repo check failed. Please try again in a moment.
        </p>
      )}
      {refresh.isSuccess && (
        <div className="border-border bg-card mt-4 rounded-lg border p-4 text-sm">
          {outOfSync.length === 0 ? (
            <p className="text-muted">
              Repo check complete: every project&apos;s GitHub repository was found.
            </p>
          ) : (
            <>
              <p className="text-destructive font-medium">
                {outOfSync.length} project{outOfSync.length !== 1 ? "s" : ""} out of sync (GitHub
                repo not found):
              </p>
              <ul className="text-muted mt-2 list-inside list-disc">
                {outOfSync.map((p) => (
                  <li key={p.id}>
                    {p.project_name} <span className="text-xs">({p.project_slug})</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      {promoteError && <p className="text-destructive mt-4 text-sm">{promoteError}</p>}

      {/* Table card */}
      <div className="bg-card border-border mt-6 overflow-hidden rounded-lg border">
        {/* Search + status filter inside card */}
        <div className="border-border flex flex-wrap items-center gap-3 border-b px-4 py-3">
          <div className="relative min-w-[14rem] flex-1 sm:max-w-sm">
            <Search className="text-muted pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
            <Input
              placeholder="Search by project, slug, or owner..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="min-w-[9rem] flex-1 sm:w-44 sm:flex-none">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value={STATUS_PARTLY_REMOVED}>
                Partly removed{partlyRemovedCount > 0 ? ` (${partlyRemovedCount})` : ""}
              </SelectItem>
              <SelectItem value={STATUS_ARCHIVED}>
                Archived{archivedCount > 0 ? ` (${archivedCount})` : ""}
              </SelectItem>
              {statuses.map((s) => {
                const label = s.replace(/_/g, " ");
                return (
                  <SelectItem key={s} value={s}>
                    {label.charAt(0).toUpperCase() + label.slice(1)}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          <Select value={ownerFilter} onValueChange={setOwnerFilter}>
            <SelectTrigger className="min-w-[9rem] flex-1 sm:w-44 sm:flex-none">
              <SelectValue placeholder="All owners" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All owners</SelectItem>
              {owners.map(([key, label]) => (
                <SelectItem key={key} value={key}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <ClientFilterSelect
            value={clientFilter}
            onChange={setClientFilter}
            className="min-w-[9rem] flex-1 sm:w-44 sm:flex-none"
          />
          {/* Says how much the "Last login" column can be trusted: the sweep
              runs on demand, so a column nobody refreshed is visibly old
              rather than quietly wrong. */}
          <p
            className={cn(
              "ml-auto text-xs whitespace-nowrap",
              refreshLastLogins.isError ? "text-destructive" : "text-muted",
            )}
            title={loginSweepDetail}
          >
            {loginFreshness}
          </p>
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-muted py-16 text-center text-sm">Loading projects...</div>
        ) : (
          <ProjectTable
            projects={filtered}
            sortBy={sortBy}
            sortDir={sortDir}
            onSort={onSort}
            onDelete={setDeleteProject}
            onArchive={setArchiveProject}
            onSetClient={handleSetClient}
            onPromoteToProd={handlePromoteToProd}
            promotingId={promotingId}
            loginCheckStaleMs={LOGIN_CHECK_STALE_MS}
          />
        )}
      </div>

      <ProjectDeleteDialog
        project={deleteProject}
        open={deleteProject !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteProject(null);
          }
        }}
      />

      <ProjectArchiveDialog
        project={archiveProject}
        open={archiveProject !== null}
        onOpenChange={(open) => {
          if (!open) {
            setArchiveProject(null);
          }
        }}
      />
    </div>
  );
}
